import React, { useEffect, useState } from "react";
import { Check } from "lucide-react";
import { PageHero } from "@/components/Shared";
import api from "@/lib/api";
import { ppMeta as M, payout, points, structure } from "@/pages/pointsPayoutsContent";

const ORD = ["1st", "2nd", "3rd", "4th", "5th", "6th", "7th", "8th", "9th", "10th"];
const H3 = ({ children }) => <h3 className="font-display text-xl font-bold text-white mt-10 mb-3">{children}</h3>;
const P = ({ children }) => <p className="text-slate-300 leading-relaxed mt-3">{children}</p>;

function Ticks({ list }) {
  return (
    <ul className="mt-4 space-y-3">
      {list.map((li) => <li key={li} className="flex gap-3 text-slate-300"><Check className="w-4 h-4 text-gold flex-shrink-0 mt-1" /><span>{li}</span></li>)}
    </ul>
  );
}

function Table({ head, rows, testId, highlightLast }) {
  return (
    <div className="mt-4 border border-white/10 overflow-x-auto" data-testid={testId}>
      <table className="w-full text-left text-sm min-w-max">
        <thead className="bg-secondary"><tr>{head.map((h) => <th key={h} className="px-4 py-3 text-gold text-xs uppercase tracking-wider">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r[0]} className="border-t border-white/5 hover:bg-white/5">
              {r.map((c, k) => <td key={k} className={`px-4 py-2.5 ${k === 0 ? "text-white font-medium" : highlightLast && k === r.length - 1 ? "text-gold font-bold" : "text-slate-300"}`}>{c}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-28" data-testid={`pp-section-${id}`}>
      <div className="border-b border-gold/20 pb-3">
        <h2 className="font-display text-2xl sm:text-3xl font-bold text-gold">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function PayoutSection() {
  return (
    <Section id="payouts" title="Tournament Payout Structure">
      <P>At Legends Poker:</P>
      <Ticks list={payout.intro} />
      <H3>Tournament Minimums</H3>
      {payout.minimums.map((t) => <P key={t}>{t}</P>)}
      <H3>Standard Prize Pool Allocation</H3>
      <P>Unless otherwise advertised:</P>
      <div className="grid sm:grid-cols-2 gap-4 mt-4">
        {payout.allocation.map((a) => (
          <div key={a.pct} className="bg-navy-paper border border-gold/30 p-5">
            <div className="font-display text-4xl font-bold text-gold">{a.pct}</div>
            <p className="text-slate-300 text-sm mt-2">{a.d}</p>
          </div>
        ))}
      </div>
      <p className="mt-4 border-l-2 border-gold bg-gold/5 px-5 py-3 text-slate-200">This ensures that 100% of player contributions are returned to players.</p>
      <H3>Standard Tournament Prize Pool Payouts</H3>
      <P>{payout.scheduleNote}</P>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6" data-testid="payout-tiers">
        {payout.tiers.map((t) => (
          <div key={t.field} className="bg-navy-paper border border-slate-800 hover:border-gold/40 transition-colors">
            <div className="px-4 py-3 border-b border-white/10 text-gold text-xs font-bold uppercase tracking-widest">{t.field}</div>
            <table className="w-full text-sm">
              <thead><tr className="text-slate-500 text-[10px] uppercase tracking-widest"><th className="px-4 pt-2 text-left font-normal">Position</th><th className="px-4 pt-2 text-right font-normal">Prize Pool %</th></tr></thead>
              <tbody>
                {t.pays.map((p, i) => <tr key={i}><td className="px-4 py-1.5 text-white">{ORD[i]}</td><td className="px-4 py-1.5 text-right text-slate-300 font-mono">{p}</td></tr>)}
              </tbody>
            </table>
            <div className="pb-2" />
          </div>
        ))}
      </div>
    </Section>
  );
}

function PointsSection() {
  return (
    <Section id="points" title="Points System">
      {points.intro.map((t) => <P key={t}>{t}</P>)}
      <Ticks list={points.bullets} />
      <H3>Base Points</H3>
      <P>{points.baseText}</P>
      <div className="grid grid-cols-3 gap-3 mt-4">
        {points.base.map((b) => (
          <div key={b.l} className="bg-navy-paper border border-gold/30 p-4 text-center">
            <div className="font-display text-3xl sm:text-4xl font-bold text-gold">{b.pts}</div>
            <div className="text-white text-sm font-medium mt-1">{b.l}</div>
            <div className="text-slate-500 text-xs">{b.d}</div>
          </div>
        ))}
      </div>
      <H3>Top 10 Bonus Points</H3>
      <P>{points.bonusText}</P>
      <div className="mt-4 bg-gold/10 border border-gold/40 px-5 py-4 text-center text-gold font-medium" data-testid="points-formula">{points.formula}</div>
      <Table head={["Position", "Multiplier"]} rows={points.multipliers} testId="points-multipliers" />
      <H3>Examples</H3>
      {points.examples.map((ex) => (
        <div key={ex.title} className="mt-6">
          <div className="text-gold text-xs uppercase tracking-[0.3em]">{ex.title}</div>
          <div className="text-white mt-1">Tournament Field: <span className="font-bold">{ex.field}</span></div>
          <Ticks list={ex.summary} />
          <Table head={points.exampleHead} rows={ex.rows} testId={`points-example-${ex.field.split(" ")[0]}`} highlightLast />
        </div>
      ))}
    </Section>
  );
}

export default function PointsPayouts() {
  const [show, setShow] = useState(null);
  useEffect(() => {
    Promise.all(["/payout-formula", "/points-formula"].map((e) => api.get(e).then((r) => !!r.data.show_public).catch(() => false)))
      .then(([payoutOn, pointsOn]) => setShow({ payoutOn, pointsOn }));
  }, []);
  if (!show) return null;
  const parts = [show.payoutOn && "payout", show.pointsOn && "points", "structure"].filter(Boolean);
  return (
    <div data-testid="info-page-points-payouts">
      <PageHero title={M.title} subtitle={M.subtitle} testId="info-hero-points-payouts" />
      <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-20 space-y-16">
        {parts.map((p) => p === "payout" ? <PayoutSection key={p} /> : p === "points" ? <PointsSection key={p} /> : (
          <Section key={p} id="structures" title="Tournament Structures">
            {structure.map((t, j) => j === 0
              ? <p key={t} className="mt-5 font-display text-xl text-white leading-relaxed">{t}</p>
              : <P key={t}>{t}</P>)}
          </Section>
        ))}
      </div>
    </div>
  );
}
